import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import emailjs from 'emailjs-com';
import Header from '../components/Header'; 
import Locations from '../components/Locations';
import Dates from '../components/Dates';
import TimeSlots from '../components/TimeSlots';
import { UMSSBlue, UMSSBlueDark } from "../utils/colors";
import GoogleSvg from "../Google.svg";
import AppleSvg from "../Apple.svg";
import { LocationContext } from '../components/LocationContext';
import { app } from '../firebase';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc 
} from "firebase/firestore";

const styles = {
  pageContainer: {
    fontFamily: 'Quicksand, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  contentContainer: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '20px',
  },
  title: {
    fontSize: '24px',
    marginBottom: '10px',
    textAlign: 'center',
    fontFamily: 'Quicksand, sans-serif',
  },
  subtitle: {
    fontSize: '16px',
    marginBottom: '20px',
    color: '#777',
    textAlign: 'center',
    fontFamily: 'Quicksand, sans-serif',
  },
  sectionCard: {
    backgroundColor: '#fff',
    padding: '20px',
    marginBottom: '20px',
    borderRadius: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    fontFamily: 'Quicksand, sans-serif',
  },
  cardTitle: {
    fontSize: '18px',
    marginBottom: '10px',
    fontFamily: 'Quicksand, sans-serif',
  },
  cardText: {
    marginBottom: '8px',
    fontSize: '14px',
    fontFamily: 'Quicksand, sans-serif',
  },
  successAlert: {
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    color: '#155724',
    padding: '20px',
    borderRadius: '5px',
    marginBottom: '20px',
    fontFamily: 'Quicksand, sans-serif',
  },
  errorAlert: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    color: '#721c24',
    padding: '20px',
    borderRadius: '5px',
    marginBottom: '20px',
    fontFamily: 'Quicksand, sans-serif',
  },
  formGroup: {
    marginBottom: '20px',
    fontFamily: 'Quicksand, sans-serif',
  },
  button: {
    backgroundColor: UMSSBlue,
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontFamily: 'Quicksand, sans-serif',
  },
  disabledButton: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    fontFamily: 'Quicksand, sans-serif',
  },
  loadingMessage: {
    textAlign: 'center',
    fontSize: '16px',
    marginTop: '50px',
    fontFamily: 'Quicksand, sans-serif',
  },
  googleCalendarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "2px solid #4285F4",
    color: "#000",
    borderRadius: "10px",
    fontSize: "1.1rem",
    fontWeight: "600",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.1s ease, background-color 0.2s ease",
    fontFamily: "Quicksand, sans-serif",
  },
  googleCalendarButtonHover: {
    backgroundColor: UMSSBlueDark,
    color: "#fff",
  },
  appleCalendarButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "12px 24px",
    backgroundColor: "#000",
    border: "2px solid #000",
    color: "#fff",
    borderRadius: "10px",
    fontSize: "1.1rem",
    fontWeight: "600",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.1s ease, background-color 0.2s ease",
    fontFamily: "Quicksand, sans-serif",
  },
  appleCalendarButtonHover: {
    backgroundColor: "#333",
  },
  calendarButtonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
    marginTop: "20px",
  },
  successMessage: {
    marginTop: "20px",
    textAlign: "center",
    fontSize: "1.2rem",
    fontWeight: "500",
    color: UMSSBlue,
  },
};

const ReschedulePage = () => {
  const [loading, setLoading] = useState(true);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTimeId, setSelectedTimeId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [officeID, setOfficeID] = useState("");
  const [initialTime, setInitialTime] = useState('');
  const [hoverGoogle, setHoverGoogle] = useState(false);
  const [hoverApple, setHoverApple] = useState(false);
  const [rescheduleNotAllowed, setRescheduleNotAllowed] = useState(false);
  
  const location = useLocation();
  
  useEffect(() => {
    // Get appointment ID from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const appointmentId = queryParams.get('appointmentId');
    
    const db = getFirestore(app);
    
    if (appointmentId) {
      const fetchAppointmentDetails = async (appointmentId) => {
        setLoading(true);
        try {
          // Get all offices
          const officesRef = collection(db, 'offices');
          const officesSnapshot = await getDocs(officesRef);
          
          for (const officeDoc of officesSnapshot.docs) {
            const officeId = officeDoc.id;
            const officeData = officeDoc.data();
    
            // Construct the path to the appointments subcollection for the current office
            const appointmentDocRef = doc(db, `offices/${officeId}/appointments`, appointmentId);
            
            // Get the appointment document
            const appointmentSnapshot = await getDoc(appointmentDocRef);
    
            if (appointmentSnapshot.exists()) {
              // Appointment found, extract the data
              const appointmentData = appointmentSnapshot.data();
    
              setOfficeID(officeId);
    
              // Check if the appointment is booked
              if (!appointmentData.booked) {
                console.log("Appointment is not booked, rescheduling not allowed.");
                setRescheduleNotAllowed(true);
                setLoading(false);
                return;
              }
    
              console.log("Appointment Details:", { id: appointmentSnapshot.id, ...appointmentData });
    
              // Store the office name & address in the state
              const appointmentWithOfficeDetails = {
                id: appointmentSnapshot.id,
                ...appointmentData,
                officeName: officeData.name,      
                officeAddress: officeData.address 
              };
              
              setAppointmentDetails(appointmentWithOfficeDetails);
              
              // Pre-select only the current location, not date or time
              setSelectedLocation({
                id: officeId,
                name: officeData.name,
                address: officeData.address
              });
              
              // Store appointment date and time for display only, don't pre-select them
              setLoading(false);
              return; // Exit the loop once the appointment is found
            }
          }
    
          // If the loop completes without finding the appointment, it doesn't exist
          console.log("Appointment not found in any office.");
          setLoading(false);
        } catch (error) {
          console.error("Error fetching appointment details:", error);
          setLoading(false);
        }
      };
      
      fetchAppointmentDetails(appointmentId);
    } else {
      setLoading(false);
    }
  }, [location]);

  // Safely convert to Date object if dateTime exists:
  const dateObj = appointmentDetails?.dateTime?.toDate();

  // Format date and time for display:
  const formattedDate = dateObj
    ? dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : 'N/A';

  const formattedTime = dateObj
    ? dateObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    : 'N/A';

  const handleLocationSelect = (loc) => {
    setSelectedLocation(loc);
    // If user re-selects the same location as the current appointment, keep date
    // Otherwise clear the date
    if (selectedDate && loc?.id === officeID) {
      // keep the existing date
    } else {
      setSelectedDate(null);
    }
    // Always clear time selection
    setSelectedTime('');
    setSelectedTimeId(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Clear previous time selection when date changes
    setSelectedTime('');
    setSelectedTimeId(null);
  };

  const handleTimeSelect = ({ time, id }) => {
    setSelectedTime(time);
    setSelectedTimeId(id);
  };

  // Function to send appointment details via email using EmailJS
  const sendAppointmentEmail = (appointmentInfo) => {
    const { patient, location, selectedDate, selectedTime, rescheduleURL, cancelURL } = appointmentInfo;
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userID = process.env.REACT_APP_EMAILJS_USER_ID;
  
    console.log("EmailJS Config:", {
      serviceID,
      templateID,
      userID: userID ? "exists" : "missing"
    });
  
    if (!serviceID || !templateID || !userID) {
      console.error("Missing EmailJS configuration. Please check your environment variables.");
      return Promise.reject(new Error("Email service not properly configured"));
    }
  
    if (!patient || !patient.email) {
      console.error("Patient email is missing.");
      return Promise.reject(new Error("Patient email is missing"));
    }
  
    const templateParams = {
      to_email: patient.email,
      patient_name: patient.patientName || "Patient",
      appointment_date: selectedDate ? selectedDate.toLocaleDateString() : "N/A",
      appointment_time: selectedTime,
      location_name: location.name,
      location_address: location.address,
      reschedule_url: rescheduleURL,
      cancel_url: cancelURL,
      is_reschedule: "true", // Indicate this is a rescheduled appointment
    };
  
    return emailjs
      .send(serviceID, templateID, templateParams, userID)
      .then((response) => {
        console.log("Email sent successfully:", response.status, response.text);
        return response;
      })
      .catch((error) => {
        console.error("Failed to send email. Details:", error);
        throw error;
      });
  };

  const handleAddGoogleCalendar = () => {
    const appointmentDurationMinutes = 30; // For example, a 30-minute appointment
    
    // Parse selectedTime (assumed format: "h:mm A", e.g., "10:00 AM")
    const [time, meridiem] = selectedTime.split(" ");
    let [hoursStr, minutesStr] = time.split(":");
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }
    
    // Construct a Date object for the start time using the selected date and time.
    const startDateTime = new Date(selectedDate);
    startDateTime.setHours(hours, minutes, 0, 0);
    
    // Compute the end time
    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + appointmentDurationMinutes);
    
    // Format dates for Google Calendar: YYYYMMDDTHHMMSSZ.
    const formatForGoogle = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };
    const startStr = formatForGoogle(startDateTime);
    const endStr = formatForGoogle(endDateTime);
    
    // Build event details
    const title = encodeURIComponent("Appointment at " + (selectedLocation.name || ""));
    const details = encodeURIComponent("Your rescheduled appointment.");
    const locationStr = encodeURIComponent(selectedLocation.address || "");
    
    // Construct the Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startStr}/${endStr}&details=${details}&location=${locationStr}`;
    
    window.open(googleCalendarUrl, "_blank");
  };

  const handleAddAppleCalendar = () => {
    const appointmentDurationMinutes = 30;
    
    const [time, meridiem] = selectedTime.split(" ");
    let [hoursStr, minutesStr] = time.split(":");
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }
    
    const startDateTime = new Date(selectedDate);
    startDateTime.setHours(hours, minutes, 0, 0);
    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + appointmentDurationMinutes);
    
    const formatICS = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };
    const startICS = formatICS(startDateTime);
    const endICS = formatICS(endDateTime);
    
    const title = "Appointment at " + (selectedLocation.name || "");
    const description = "Your rescheduled appointment.";
    const locationStr = selectedLocation.address || "";
    
    const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startICS}
DTEND:${endICS}
SUMMARY:${title}
DESCRIPTION:${description}
LOCATION:${locationStr}
END:VEVENT
END:VCALENDAR
    `.trim();
    
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rescheduled_appointment.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleConfirmReschedule = async () => {
    // This runs only when user clicks the confirm button
    if (!selectedLocation || !selectedDate || !selectedTimeId) {
      return;
    }
    setLoading(true);

    try {
      const db = getFirestore(app);

      // 1) Reference the old doc (currently booked)
      const oldDocRef = doc(db, `offices/${officeID}/appointments`, appointmentDetails.id);
      const oldDocSnap = await getDoc(oldDocRef);
      if (!oldDocSnap.exists()) {
        throw new Error("Old appointment doc not found");
      }
      const oldDocData = oldDocSnap.data();

      // 2) Reference the new doc (currently unbooked) that the user selected
      const newDocRef = doc(db, `offices/${selectedLocation.id}/appointments`, selectedTimeId);
      const newDocSnap = await getDoc(newDocRef);
      if (!newDocSnap.exists()) {
        throw new Error("New appointment doc not found");
      }
      const newDocData = newDocSnap.data();

      // 3) Prepare updated data for the new doc:
      //    - Copy all relevant fields from the old doc, but keep the new doc's dateTime
      //    - Mark it booked, and mark rescheduled = true
      const updatedNewDocData = {
        ...oldDocData,
        dateTime: newDocData.dateTime,  // keep the new dateTime
        booked: true,
        rescheduled: true
      };

      // 4) Prepare updated data for the old doc so it becomes a fresh unbooked slot
      //    (i.e., clear out patient info, mark booked=false, etc.)
      const updatedOldDocData = {
        booked: false,
        canceled: false,
        dateTime: oldDocData.dateTime, // keep the old date/time on the old doc
        isCheckedIn: false,
        isWalkIn: true,
        patientId: "",
        patientName: "",
        rescheduled: false,
        seenDoctor: false,
        vitalsDone: false
      };

      // 5) Update both documents
      await updateDoc(oldDocRef, updatedOldDocData);
      await updateDoc(newDocRef, updatedNewDocData);

      // 6) Done
      setLoading(false);
      setSuccess(true);

      // Send email notification
      try {
        const domain = "https://umss-appts-d5ea54e435bd.herokuapp.com";
        const rescheduleURL = `${domain}/reschedule?appointmentId=${selectedTimeId}`;
        const cancelURL = `${domain}/cancel?appointmentId=${selectedTimeId}`;

        // Get the patient ID from the appointment
        const patientId = oldDocData.patientId;
        
        if (!patientId) {
          throw new Error("Patient ID is missing from the appointment");
        }

        // Get the patient document from the patients collection
        const patientDocRef = doc(db, "patients", patientId);
        const patientDocSnap = await getDoc(patientDocRef);
        
        if (!patientDocSnap.exists()) {
          throw new Error("Patient document not found");
        }
        
        const patientData = patientDocSnap.data();
        const patientEmail = patientData.email;
        
        if (!patientEmail) {
          throw new Error("Email is missing from the patient document");
        }

        await sendAppointmentEmail({
          patient: {
            email: patientEmail,
            patientName: appointmentDetails.patientName
          },
          location: selectedLocation,
          selectedDate,
          selectedTime,
          rescheduleURL,
          cancelURL
        });
        console.log("Rescheduling email notification sent successfully");
      } catch (emailError) {
        console.error("Email notification failed:", emailError);
        console.warn("Appointment rescheduled successfully, but failed to send email notification:", emailError);
      }

    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      setLoading(false);
      // Optionally show an error message
    }
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <Header />
        <div style={styles.contentContainer}>
          <p style={styles.loadingMessage}>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (rescheduleNotAllowed) {
    return (
      <div style={styles.pageContainer}>
        <Header />
        <div style={styles.contentContainer}>
          <div style={styles.errorAlert}>
            This appointment cannot be rescheduled. It may have been canceled or already completed.
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.pageContainer}>
        <Header />
        <div style={styles.contentContainer}>
          <div style={styles.successAlert}>
            Your appointment has been successfully rescheduled for{' '}
            {selectedDate ? selectedDate.toLocaleDateString() : ''} at {selectedTime}. 
            You will receive an email confirmation shortly.
          </div>
          <p style={styles.successMessage}>
            Please check your email for confirmation details.
          </p>
          <div style={styles.calendarButtonsContainer}>
            <button
              onClick={handleAddGoogleCalendar}
              style={{
                ...styles.googleCalendarButton,
                ...(hoverGoogle ? styles.googleCalendarButtonHover : {}),
              }}
              onMouseEnter={() => setHoverGoogle(true)}
              onMouseLeave={() => setHoverGoogle(false)}
            >
              <img
                src={GoogleSvg}
                alt="Google Calendar"
                style={{ width: "20px", height: "20px" }}
              />
              Add to Google Calendar
            </button>
            <button
              onClick={handleAddAppleCalendar}
              style={{
                ...styles.appleCalendarButton,
                ...(hoverApple ? styles.appleCalendarButtonHover : {}),
              }}
              onMouseEnter={() => setHoverApple(true)}
              onMouseLeave={() => setHoverApple(false)}
            >
              <img
                src={AppleSvg}
                alt="Apple Calendar"
                style={{ width: "20px", height: "20px", filter: "invert(1)" }}
              />
              Add to Apple Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <Header />
      <div style={styles.contentContainer}>
        <h1 style={styles.title}>Reschedule Your Appointment</h1>
        <p style={styles.subtitle}>Please select a new time for your appointment.</p>
        
        {appointmentDetails ? (
          <div style={styles.sectionCard}>
            <h3 style={styles.cardTitle}>Current Appointment</h3>
            <p style={styles.cardText}>Office: {appointmentDetails.officeName}</p>
            <p style={styles.cardText}>Address: {appointmentDetails.officeAddress}</p>
            <p style={styles.cardText}>Date: {formattedDate}</p>
            <p style={styles.cardText}>Time: {formattedTime}</p>
          </div>
        ) : (
          <div style={styles.errorAlert}>
            No appointment details found. Please check your email link.
          </div>
        )}

        {/* Instead of a <form> with onSubmit, use a regular div so clicking doesn't auto-submit */}
        <div>
          <div style={styles.sectionCard}>
            <h3 style={styles.cardTitle}>Select a New Time</h3>

            <LocationContext.Provider value={{ selectedLocation, setSelectedLocation }}>
              {/* Location */}
              <div style={styles.formGroup}>
                <h4>Location</h4>
                <Locations onSelect={handleLocationSelect} initialLocation={selectedLocation} />
              </div>
              
              {/* Date */}
              {selectedLocation && (
                <div style={styles.formGroup}>
                  <h4>Date</h4>
                  <Dates onSelect={handleDateSelect} initialDate={selectedDate} />
                </div>
              )}

              {/* Time Slots */}
              {selectedDate && selectedLocation && (
                <div style={styles.formGroup}>
                  <h4>Time</h4>
                  <TimeSlots
                    onSelect={handleTimeSelect}
                    selectedTime={selectedTime}
                    selectedDate={selectedDate}
                    initialTime={initialTime}
                  />
                </div>
              )}
            </LocationContext.Provider>
          </div>

          {/* "Confirm Reschedule" is now a normal button, not a form submit */}
          <button
            type="button"
            style={{
              ...styles.button,
              ...( !selectedLocation || !selectedDate || !selectedTime ? styles.disabledButton : {} ),
            }}
            disabled={!selectedLocation || !selectedDate || !selectedTime}
            onClick={handleConfirmReschedule}
          >
            Confirm Reschedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReschedulePage;